import ClearIcon from '@mui/icons-material/Clear';
import { Box, Button, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import debounce from 'lodash/debounce';
import React, { FC, useCallback, useEffect, useState } from 'react';

import { IOptionType, Select } from '../../components';
import { ClearAdornment } from '../../components/adornments';
import { INetSuiteLogFilters } from '../../models';
import { theme } from '../../styles';

interface INetSuiteLogFilterProps {
  handleFilter: (filters: INetSuiteLogFilters) => void;
  isLoading: boolean;
  entityFilterOptions: IOptionType[];
  actionFilterOptions: IOptionType[];
}

export const NetSuiteLogFilters: FC<INetSuiteLogFilterProps> = ({
  handleFilter,
  isLoading,
  entityFilterOptions,
  actionFilterOptions,
}) => {
  const classes = useStyles();

  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [netsuiteReference, setNetsuiteReference] = useState<string>('');
  const [entity, setEntity] = useState<string>('');
  const [action, setAction] = useState<string>('');
  const [account, setAccount] = useState<string>('');

  // useCallback prevents this from being initialized
  // more than once which allows debounce to work properly.
  // Also we pass in a param to this otherwise we won't have
  // the correct reference and will use initial value of the filter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterCallback = useCallback(
    debounce(
      (
        invoiceFilter: string,
        netsuiteReferenceFilter: string,
        entityFilter: string,
        actionFilter: string,
        accountFilter: string
      ) => {
        handleFilter({
          invoiceNumber: invoiceFilter.trim(),
          netsuiteReference: netsuiteReferenceFilter.trim(),
          entity: entityFilter.trim(),
          action: actionFilter.trim(),
          account: accountFilter.trim(),
        });
      },
      500
    ),
    []
  );

  useEffect(() => {
    if (!isLoading) {
      filterCallback(invoiceNumber, netsuiteReference, entity, action, account);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNumber, netsuiteReference, action, account, entity]);

  return (
    <Box
      sx={theme => ({
        display: 'flex',
        alignItems: 'center',

        [theme.breakpoints.down('sm')]: {
          flexDirection: 'column',
          alignItems: 'flex-start',
        },

        // Let the container take care of consistent spacing between elements.
        '& > *:not(:last-child)': {
          marginRight: theme.spacing(1),

          [theme.breakpoints.down('sm')]: {
            marginBottom: theme.spacing(1),
          },
        },
      })}
    >
      {/* INVOICE FILTER */}
      <TextField
        label="Invoice"
        className={classes.filter}
        value={invoiceNumber}
        onChange={({ target: { value } }) => setInvoiceNumber(value)}
        InputProps={{
          endAdornment: (
            <ClearAdornment onClick={() => setInvoiceNumber('')} show={!!invoiceNumber} />
          ),
        }}
      />

      {/* NETSUITE REFERENCE FILTER */}
      <TextField
        size="small"
        label="NetSuite Reference"
        className={classes.filter}
        value={netsuiteReference}
        onChange={({ target: { value } }) => setNetsuiteReference(value)}
        InputProps={{
          endAdornment: (
            <ClearAdornment onClick={() => setNetsuiteReference('')} show={!!netsuiteReference} />
          ),
        }}
      />

      {/* ENTITY FILTER */}
      <Select
        size="small"
        className={classes.filter}
        label="Entity"
        id="entity-filter"
        value={entity}
        onChange={e => setEntity(e.target.value)}
        options={entityFilterOptions}
        customClearFunction={() => setEntity('')}
        style={{
          fontSize: '13px',
        }}
        inputLabelStyle={{
          fontSize: '14px',
        }}
      />

      {/* ACTION FILTER */}
      <Select
        size="small"
        label="Action"
        className={classes.filter}
        id="action-filter"
        value={action}
        onChange={e => setAction(e.target.value)}
        options={actionFilterOptions}
        customClearFunction={() => setAction('')}
      />

      {/* ACCOUNT */}
      <TextField
        size="small"
        label="Account"
        className={classes.filter}
        value={account}
        onChange={({ target: { value } }) => setAccount(value)}
        InputProps={{
          endAdornment: <ClearAdornment onClick={() => setAccount('')} show={!!account} />,
        }}
      />

      {/* CLEAR ALL BUTTON */}
      {(!!invoiceNumber || !!netsuiteReference || !!action || !!entity || !!account) && (
        <Button
          startIcon={<ClearIcon />}
          sx={theme => ({
            backgroundColor: theme.palette.common.white,
            [theme.breakpoints.down('md')]: {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
            },
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
          })}
          onClick={() => {
            setInvoiceNumber('');
            setNetsuiteReference('');
            setAction('');
            setAccount('');
            setEntity('');
          }}
        >
          Reset
        </Button>
      )}
    </Box>
  );
};

const useStyles = makeStyles(() => {
  return {
    filter: {
      width: theme.spacing(12),
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
  };
});
